import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { useLang } from "../../lib/i18n";
import { useTestimonials } from "../../lib/queries";
import { optimizeImg } from "../../lib/img";

export default function Testimonials() {
  const { data: items = [] } = useTestimonials();
  const [idx, setIdx] = useState(0);
  const { t, pick } = useLang();

  useEffect(() => {
    if (!items.length) return;
    const id = setInterval(() => setIdx((i) => (i + 1) % items.length), 6000);
    return () => clearInterval(id);
  }, [items.length]);

  if (!items.length) return null;
  const cur = items[idx];

  return (
    <section id="testimonials" data-testid="testimonials-section" className="py-32 relative">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="mb-16">
          <div className="flex items-center gap-3 mb-4">
            <span className="w-8 h-px bg-[#A855F7]" />
            <span className="font-mono-ui text-[10px] tracking-[0.3em] text-[#A855F7] uppercase">
              {t("testimonials_kicker")}
            </span>
          </div>
          <h2 className="font-display text-5xl md:text-6xl lg:text-7xl uppercase tracking-tighter text-white">
            {t("testimonials_title")}
          </h2>
        </div>

        <div className="relative rounded-3xl border border-white/5 bg-[#101018]/70 p-10 md:p-16 overflow-hidden">
          <Quote size={140} className="absolute -top-6 -left-6 text-[#A855F7]/8" />
          <AnimatePresence mode="wait">
            <motion.div
              key={cur.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className="relative"
            >
              <div className="flex items-center gap-1 mb-6">
                {Array.from({ length: cur.rating }).map((_, i) => (
                  <Star key={i} size={16} fill="#A855F7" stroke="#A855F7" />
                ))}
              </div>
              <p className="font-display text-2xl md:text-4xl leading-tight text-white max-w-4xl uppercase tracking-tight">
                “{pick(cur, "quote")}”
              </p>
              <div className="mt-10 flex items-center gap-4">
                {cur.avatar_url && (
                  <img src={optimizeImg(cur.avatar_url, 100, 80)} alt="" loading="lazy" decoding="async" className="w-12 h-12 rounded-full object-cover border border-white/10" />
                )}
                <div>
                  <div className="text-white font-medium">{cur.customer_name}</div>
                  <div className="text-white/50 text-sm">{cur.company}</div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          <div className="mt-12 flex items-center justify-between">
            <div className="flex items-center gap-2">
              {items.map((_, i) => (
                <button
                  key={i}
                  data-testid={`testimonial-dot-${i}`}
                  onClick={() => setIdx(i)}
                  className={`h-1.5 transition-all duration-300 rounded-full ${
                    i === idx ? "bg-[#A855F7] w-10" : "bg-white/20 w-4"
                  }`}
                />
              ))}
            </div>
            <div className="flex items-center gap-3">
              <button
                data-testid="testimonial-prev"
                onClick={() => setIdx((i) => (i - 1 + items.length) % items.length)}
                className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-white hover:border-[#A855F7]/60 transition-colors"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                data-testid="testimonial-next"
                onClick={() => setIdx((i) => (i + 1) % items.length)}
                className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-white hover:border-[#A855F7]/60 transition-colors"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
